import { z } from 'zod';

/**
 * Chaos power-up contract shared by the room protocol, the stored round and the client.
 * Timing rules and effects live in `game/power-ups.ts`; this file only names the shapes.
 *
 * Self boosts: `gold` (bonus on the consuming word), `double` (timed multiplier),
 * `reveal` (one unfound word, sent privately), `shield` (absorbs the next debuff).
 * Debuffs on every other participant: `freeze` (words refused), `tax` (timed half points),
 * `spin` and `fog` (client presentation only; the board's letters and rules are unchanged).
 */
export const POWER_UP_KINDS = ['gold', 'double', 'reveal', 'shield', 'freeze', 'tax', 'spin', 'fog'] as const;
export type PowerUpKind = (typeof POWER_UP_KINDS)[number];

/** Kinds that stay on a participant until a deadline; the rest resolve when consumed. */
export const TIMED_EFFECT_KINDS = ['double', 'freeze', 'tax', 'spin', 'fog'] as const;
export type TimedEffectKind = (typeof TIMED_EFFECT_KINDS)[number];

/** Debuffs land on everyone except the collector; boosts land on the collector alone. */
export const DEBUFF_KINDS = ['freeze', 'tax', 'spin', 'fog'] as const satisfies readonly PowerUpKind[];

/** Largest board is 5×5, so tile indices never exceed 24. */
const MAX_TILE_INDEX = 24;

export const powerUpSpawnSchema = z.object({
  id: z.string().max(16),
  kind: z.enum(POWER_UP_KINDS),
  tile: z.number().int().min(0).max(MAX_TILE_INDEX),
  /** Server milliseconds: visible from `at`, gone after `until` unless consumed first. */
  at: z.number(),
  until: z.number(),
  /** Participant who consumed it; absent while it is still on the board. */
  takenBy: z.string().max(80).optional(),
});
export type PowerUpSpawn = z.infer<typeof powerUpSpawnSchema>;

export const activeEffectSchema = z.object({
  kind: z.enum(TIMED_EFFECT_KINDS),
  until: z.number(),
});
export type ActiveEffect = z.infer<typeof activeEffectSchema>;

/** Visible and collectable at server time `now`; the board and the server judge spawns by this alone. */
export function isSpawnLive(spawn: Readonly<PowerUpSpawn>, now: number): boolean {
  return spawn.takenBy === undefined && spawn.at <= now && now <= spawn.until;
}

export function isTimedEffectKind(kind: PowerUpKind): kind is TimedEffectKind {
  return (TIMED_EFFECT_KINDS as readonly string[]).includes(kind);
}

export function isDebuffKind(kind: PowerUpKind): boolean {
  return (DEBUFF_KINDS as readonly string[]).includes(kind);
}
